import { CheckCircleOutlined, CloseCircleOutlined, CrownOutlined, GithubOutlined, GoogleOutlined, HistoryOutlined, MailOutlined, PlusOutlined, SecurityScanOutlined, LockOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Button, message, Divider, Tooltip, Input, Form, Tag, Dropdown, Modal } from 'antd';
import React, { useState, useRef, Fragment, useEffect } from 'react';
import { PageContainer, FooterToolbar } from '@ant-design/pro-layout';
import type { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import ProForm, { ModalForm, ProFormText, ProFormTextArea, ProFormCheckbox, ProFormSelect } from '@ant-design/pro-form';
import { useModel } from '@umijs/max';
import {
  users,
  addUser,
  delUser,
  updateUser,
  enableUsers,
  inviteUser,
  reinviteUser,
  smtpConfigValidate,
  getUsedCount,
  disableLoginVerification,
  updateUserTFAEnforce,
  forceLogout,
  setUsersNote,
} from '@/services/api';
import type { API } from '@/services/typings';
import type { PwdStrengthLevel } from '../../components/PwdStrength';
import { PwdStrength, getPwdStrength } from '../../components/PwdStrength';
import { t, renderDetails, getTablePaginationConfig, GroupTypeUser, NamesPurpose_ChangeUserGroup, NamesPurpose_CreateUser, NamesPurpose_InviteUser, NamesPurpose_UpdateUser, oidcProviderDisplayName } from '../../global';
import styles from './index.less';
import { NameEditor } from '../DeviceList/DevicesTable';
import DeviceSelect from './DeviceSelect';
import { fetchAllNames } from '../DeviceGroupList';
import { getLocalSortState, handleSortStateChange, TableSorterTooltip } from '@/components/Others/table_sort';
import { getUsedUsers, getUsedUsersColor } from '../Welcome';
import { ItemType } from 'rc-menu/lib/interface';
import SetNoteModal from '../AddressBook/DetailComponents/SetNoteModal';
import {
  hasPermUsersCreate,
  hasPermUsersInvite,
  canDeleteUser,
  canEnableDisableUser,
  canEditUserEmail,
  canEditUserPassword,
  canEditUserNote,
  canEditUserDisplayName,
  canManageUser2FA,
  canForceLogoutUser,
  hasGlobalPermAssignDeviceToUser,
  canChangeGroupOfUser,
  shouldUserListShowMultiSelect,
  canAssignControlRoleToUser,
  canAssignStrategyToUser,
} from '../RoleList/permissionUtils';

export const USER_LIST_SORT_STATE_KEY = 'user_list_sort_state';
const USER_LIST_USER_STATUS_LOGIN_VERIFICATION_STATUS_SORT_KEY = 'user_list_user_status_login_verification_status_sort_key';
const USER_LIST_NAME_SORT_KEY = 'user_list_name_sort_key';

enum UserStatus {
  Disabled = 0,
  Normal = 1,
  Pending = -1,
}

const handleAdd = async (data: API.UserListItem) => {
  const hide = message.loading(t('Adding'));
  try {
    await addUser({ data });
    hide();
    message.success(t('Added successfully'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Adding failed, please try again!'),
    );
    return false;
  }
};

const handleUpdate = async (fields: API.UserListItem, old: API.UserListItem) => {
  const data = { guid: old.guid } as any;
  const newDisplayName = (fields.display_name ?? '').trim();
  const oldDisplayName = (old.display_name ?? '').trim();
  if ((fields.email || '') != (old.email || '')) {
    data['email'] = fields.email;
  }
  if ((fields.note || '') != (old.note || '')) {
    data['note'] = fields.note;
  }
  if (newDisplayName != oldDisplayName) {
    data['display_name'] = newDisplayName;
  }
  if ((fields.group_name || '') != (old.group_name || '')) {
    data['grp'] = fields.group_name;
  }
  if (fields.password) {
    data['password'] = fields.password;
  }
  if (!!fields.is_admin != !!old.is_admin) {
    data['is_admin'] = !!fields.is_admin;
  }
  // Handle roles update
  const newRoles = fields.admin_roles || [];
  const oldRoles = old.admin_roles || [];
  const rolesChanged = JSON.stringify([...newRoles].sort()) !== JSON.stringify([...oldRoles].sort());
  if (rolesChanged) {
    data['admin_roles'] = newRoles;
  }
  if ((fields.control_role_name || '') != (old.control_role_name || '')) {
    data['control_role_name'] = fields.control_role_name;
  }
  if ((fields.strategy_name || '') != (old.strategy_name || '')) {
    data['strategy_name'] = fields.strategy_name;
  }
  if (Object.keys(data).length == 1) return false;
  const hide = message.loading(t('Updating'));
  try {
    await updateUser({ data });
    hide();

    message.success(t('Update is successful'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Update failed, please try again!'),
    );
    return false;
  }
};

const handleEnable = async (rows: API.UserListItem[], disable: boolean) => {
  const hide = message.loading(t('Updating'));
  try {
    await enableUsers({
      data: {
        rows: rows.map((x) => x.guid),
        disable,
      },
    });
    hide();

    message.success(t('Update is successful'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Enable/disable failed, please try again!'),
    );
    return false;
  }
};

export const UserPassword = (props: { required: boolean, autoFocus?: boolean, initialValue?: string }) => {
  const inputRef = useRef<InputRef>(null);
  const [pwdStrength, setPwdStengthLength] = useState<PwdStrengthLevel>(0);
  const [password, setPassword] = useState(props.initialValue ?? '');

  useEffect(() => {
    if (props.autoFocus) {
      inputRef.current?.focus();
    }
  }, [props.autoFocus]);

  return (
    <>
      <ProFormText.Password
        initialValue={password}
        rules={[
          {
            required: props.required,
            whitespace: true,
          },
          {
            min: 6,
            max: 300,
          },
        ]}
        width="md"
        name="password"
        label={t(!props.required ? 'New Password' : 'Password', true)}
        help={pwdStrength == 0 ? undefined : <PwdStrength pwdStrength={pwdStrength} />}
        fieldProps={{
          // https://github.com/ant-design/ant-design/issues/10104
          autoComplete: 'new-password',
          onChange: (e) => {
            setPassword(e.target.value);
            setPwdStengthLength(getPwdStrength(e.target.value));
          },
          ref: inputRef,
        }}
      />
      <ProFormText.Password
        initialValue={password}
        rules={[
          {
            required: props.required,
            whitespace: true,
          },
          {
            validator:
              props.required || password.length > 0
                ? (_, value) => {
                  if (!value || value === password) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Confirm password failed, please check your input'),
                  );
                }
                : undefined,
          },
        ]}
        width="md"
        name="confirm-password"
        label={t('Confirm Password', true) as string}
      />
    </>
  );
};

export const EmailEditor = (props: { required?: boolean, disabled?: boolean, onChange?: (v: string) => void }) => {
  return (
    <ProFormText
      fieldProps={{ autoComplete: 'off', onChange: (e: any) => props.onChange?.(e.target.value) }}
      rules={[
        {
          required: props.required,
          whitespace: true,
        },
        {
          max: 300,
        },
        {
          type: 'email',
        },
      ]}
      validateTrigger={['onBlur']}
      width="md"
      name="email"
      label={t('Email', true)}
      readonly={props.disabled}
    />
  );
};

export const UserFields = (props: {
  isNew: boolean,
  status?: number,
  third_auth_type?: string,
  disabled?: boolean,
  disablePassword?: boolean,
  disableEmail?: boolean,
  disableDisplayName?: boolean,
  readonlyDisplayName?: boolean,
}) => {
  return (
    <>
      <ProFormText
        fieldProps={{ autoComplete: 'off' }}
        rules={[
          {
            required: props.isNew,
            whitespace: true,
          },
          props.isNew ? {
            min: 4,
            max: 30,
          } : {},
        ]}
        width="md"
        name="name"
        readonly={!props.isNew}
        label={t('Username', true)}
      />
      <ProFormText
        fieldProps={{ autoComplete: 'off' }}
        width="md"
        name="display_name"
        readonly={props.readonlyDisplayName}
        disabled={props.disableDisplayName}
        rules={[
          {
            max: 300,
          },
        ]}
        label={t('Display Name', true)}
      />
      <ProFormText
        width="md"
        hidden={props.isNew || !props.third_auth_type}
        readonly={true}
        fieldProps={{ value: oidcProviderDisplayName(props.third_auth_type) }}
        label={t('Auth Type', true)}
      />
      {
        props.disabled || props.status === UserStatus.Pending || props.disablePassword ? null :
          <UserPassword required={props.isNew} />
      }
      <EmailEditor disabled={props.disabled || props.disableEmail} />
    </>
  );
};

type DelConfirmFormProps = {
  visible: boolean;
  handleModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  actionRef: React.MutableRefObject<ActionType | undefined>;
  initialValues: API.UserListItem | any;
  del: any;
};

export const DelConfirmForm: React.FC<DelConfirmFormProps> = ({ visible, handleModalVisible, actionRef, initialValues, del }) => {
  const [form] = Form.useForm();
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const name = initialValues['name'] || initialValues['id'];

  const checkInput = () => {
    const values = form.getFieldsValue();
    if (values.input === name) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  };

  const onFinish = async () => {
    const hide = message.loading(t('Updating'));
    try {
      await del(initialValues['guid']);
      hide();
      message.info("Successfully updated");
    } catch (err) {
      hide();
      message.error(
        typeof err == 'string' ? (err as string) : t('Deleting failed, please try again!'),
      );
    }
    actionRef.current?.reload();
    handleModalVisible(false);
  };

  return <ModalForm
    form={form}
    title={t('Confirm')}
    visible={visible}
    width="450px"
    initialValues={initialValues}
    onVisibleChange={handleModalVisible}
    submitter={{
      render: () => {
        return (
          <>
            <Button type="primary" size="middle" disabled={buttonDisabled} block htmlType="submit" onClick={onFinish}>
              {t('Delete')}
            </Button>
          </>
        );
      }
    }}>
    <div className={styles.delConfirmText}>
      {t('To confirm')},&nbsp;{t('type')}&nbsp;&quot;<b>{name}</b>&quot;&nbsp;{t("in the box below")}
    </div>
    <ProForm.Item name="input">
      <Input
        onChange={checkInput}
        onPressEnter={() => {
          checkInput();
          if (!buttonDisabled) {
            onFinish();
          }
        }}
      />
    </ProForm.Item>
  </ModalForm>;
}

export const ThirdAuthTypeTag = (auth?: string) => <>
  {auth && <Tooltip title={"Logged in with " + oidcProviderDisplayName(auth) + " account"}>
    {auth == "GOOGLE" ? <GoogleOutlined style={{ marginLeft: 6 }} />
      : auth == "GITHUB" ? <GithubOutlined style={{ marginLeft: 6 }} />
        : <Tag style={{ marginLeft: 6 }} color="green">{oidcProviderDisplayName(auth)}</Tag>}
  </Tooltip>}
</>;

export const userColumns = (props: { actionRef: any, myname: string | undefined, is_select?: boolean, onIdClick?: ((entity: API.UserListItem) => void), extraColumns?: ProColumns<API.UserListItem>[], sortState: API.SortState, setSortState: React.Dispatch<React.SetStateAction<API.SortState>> }) => {
  const nameSortKey = localStorage.getItem(USER_LIST_NAME_SORT_KEY) || 'a_name';
  const nameSortOptions = [
    { value: 'a_name', label: t('Username', true) },
    { value: 'a_role', label: t('Role', true) },
  ];

  const userStatus = 'a_status';
  const login_verification_2fa = 'login_verification_2fa';
  const login_verification_email = 'login_verification_email';
  const tfa_enforced = 'tfa_enforced';
  const userStatusloginVerificationStatusSortKey = localStorage.getItem(USER_LIST_USER_STATUS_LOGIN_VERIFICATION_STATUS_SORT_KEY) || userStatus;
  const userStatusloginVerificationStatusSortOptions = [
    { value: userStatus, label: t("user_status", true) },
    { value: login_verification_2fa, label: t("2fa_activation_status", true) },
    { value: tfa_enforced, label: t("2fa_enforcement_status", true) },
    { value: login_verification_email, label: t("email_login_verification_status", true) },
  ];

  const getUserStatus = (record: API.UserListItem) => {
    if (record.status == UserStatus.Pending) {
      return (<>
        <Tooltip title={t('user_status_pending')}>
          <HistoryOutlined style={{ color: '#faad14' }} />
        </Tooltip>
        <Tooltip title={t('resend_invitation')}>
          <MailOutlined style={{ color: '#faad14', marginLeft: 6 }} onClick={async () => {
            const hide = message.loading(t('Updating'));
            try {
              await reinviteUser({ data: { name: record.name } });
            } catch (error) {
              message.error(error as any);
            }
            hide();
          }} />
        </Tooltip>
      </>
      );
    } else if (record.status == UserStatus.Disabled) {
      return (
        <Tooltip title={t('user_status_disabled')}>
          <CloseCircleOutlined style={{ color: '#f5222d' }} />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title={t('user_status_active')}>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        </Tooltip>
      );
    }
  }

  const getLoginVerificationStatus = (record: API.UserListItem) => {
    return <>
      {
        record.tfa_enforced && record.totp_verified && (
          <Tooltip title={t('2fa_enforced_and_active', true)}>
            <LockOutlined style={{ color: '#52c41a', marginRight: 8 }} />
          </Tooltip>
        )
      }
      {
        record.tfa_enforced && !record.totp_verified && (
          <Tooltip title={t('2fa_enforced_but_pending', true)}>
            <ClockCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
          </Tooltip>
        )
      }
      {
        !record.tfa_enforced && record.totp_verified && (
          <Tooltip title={t('2fa_not_enforced_but_active', true)}>
            <SecurityScanOutlined style={{ color: '#1890ff', marginRight: 8 }} />
          </Tooltip>
        )
      }
      {
        record.email_verification && (
          <Tooltip title={t('email_login_verification_enabled', true)}>
            <MailOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        )
      }
    </>
  }

  const columns: ProColumns<API.UserListItem>[] = [
    {
      title: t('Username'),
      dataIndex: 'name',
      hideInDescriptions: true,
      render: (dom, record) => {
        const auth = record.third_auth_type;
        const primaryText = record.display_name || dom;
        const secondaryText = record.display_name ? <span style={{ fontSize: 12, color: '#999' }}>{record.name}</span> : null;
        return (<>
          {props.onIdClick ? <a onClick={() => props.onIdClick?.(record)}>{primaryText}</a> : <span>{primaryText}</span>}
          {record.is_admin && (
            <Tooltip title={t('Administrator')}>
              <CrownOutlined style={{ marginLeft: 6, color: '#faad14' }} />
            </Tooltip>
          )}
          {record.name == props.myname && <Tag style={{ marginLeft: 6 }} color="blue">{t("Me")}</Tag>}
          {ThirdAuthTypeTag(auth)}
          {secondaryText && <div>{secondaryText}</div>}
        </>
        );
      },
      key: nameSortKey,
      sorter: true,
      sortOrder: props.sortState.columnKey === nameSortKey ? props.sortState.order : undefined,
      showSorterTooltip: {
        title: <TableSorterTooltip actionRef={props.actionRef} idSortKey={USER_LIST_NAME_SORT_KEY} sortStateKey={USER_LIST_SORT_STATE_KEY} options={nameSortOptions as any} setSortState={props.setSortState} />,
      },
      hideInSearch: true,
    },
    {
      title: t('Username', true),
      dataIndex: 'name',
      hideInTable: true,
    },
    {
      title: t('Display Name', true),
      dataIndex: 'display_name',
      hideInTable: true,
      hideInSearch: true,
    },
    {
      title: t('Email'),
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'email' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      tooltip: t('login_verification_column_tip', true),
      hideInSearch: true,
      render: (text, record) => {
        return <div>
          {getUserStatus(record)}
          <span style={{ marginRight: 8 }}></span>
          {getLoginVerificationStatus(record)}
        </div>
      },
      sorter: true,
      key: userStatusloginVerificationStatusSortKey,
      sortOrder: props.sortState.columnKey === userStatusloginVerificationStatusSortKey ? props.sortState.order : undefined,
      showSorterTooltip: {
        title: <TableSorterTooltip actionRef={props.actionRef} idSortKey={USER_LIST_USER_STATUS_LOGIN_VERIFICATION_STATUS_SORT_KEY} sortStateKey={USER_LIST_SORT_STATE_KEY} options={userStatusloginVerificationStatusSortOptions as any} setSortState={props.setSortState} />,
      },
    },
    {
      title: t('Status', true),
      dataIndex: 'status',
      valueEnum: {
        1: {
          text: t('Normal'),
        },
        0: {
          text: t('Disabled'),
        },
        "-1": {
          text: t('Pending Invite'),
        },
      },
      hideInTable: true,
      hideInSearch: props.is_select,
      hideInDescriptions: true,
    },
    {
      title: t('Roles', true),
      width: 180,
      tooltip: (
        <>
          <Tag color="blue">{t('Admin Role', true)}</Tag>
          <Tag color="orange">{t('Control Role', true)}</Tag>
        </>
      ),
      render: (_, record) => {
        const roles = record.admin_roles || [];
        const controlRole = record.control_role_name;

        if (roles.length === 0 && !controlRole) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        const maxShow = 2;
        const visibleRoles = roles.slice(0, maxShow);
        const hiddenCount = roles.length - maxShow;

        const rolesContent = (
          <>
            {visibleRoles.map((name, index) => (
              <Tag
                key={`role-${name}-${index}`}
                color="blue"
                style={{ marginBottom: 4 }}
              >
                {name}
              </Tag>
            ))}
            {hiddenCount > 0 && (
              <Tag color="default" style={{ marginBottom: 4 }}>
                +{hiddenCount}
              </Tag>
            )}
          </>
        );

        const wrappedRoles =
          hiddenCount > 0 ? <Tooltip title={roles.join(', ')}>{rolesContent}</Tooltip> : rolesContent;

        return (
          <>
            {wrappedRoles}
            {controlRole && (
              <Tag color="orange" style={{ marginBottom: 4 }}>
                {controlRole}
              </Tag>
            )}
          </>
        );
      },
      showSorterTooltip: false,
      hideInSearch: true,
    },
    {
      title: t('Admin Role', true),
      dataIndex: 'admin_role_name',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Control Role', true),
      dataIndex: 'control_role_name',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Administrator', true),
      dataIndex: 'is_admin',
      valueEnum: {
        0: {
          text: t('Non-Administrator'),
        },
        1: {
          text: t('Administrator'),
        },
      },
      hideInTable: true,
      hideInSearch: props.is_select,
      hideInDescriptions: true,
    },
    {
      title: t('Strategy', true),
      dataIndex: 'strategy_name',
      tooltip: `-: ${t('Default Strategy', true)}`,
      key: 'c_name',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'c_name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
      hideInSearch: true,
    },
    {
      title: t('Strategy', true),
      dataIndex: 'strategy_name',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Group'),
      dataIndex: 'group_name',
      tip: t('groupTip', true, 'Connections within group are allowed') as string,
      key: 'b_name',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'b_name' ? props.sortState.order : undefined,
      showSorterTooltip: false,
      hideInSearch: true,
    },
    {
      title: t('Group', true),
      dataIndex: 'group_name',
      hideInTable: true,
      hideInDescriptions: true,
    },
    {
      title: t('Auth Type'),
      dataIndex: 'third_auth_type',
      hideInTable: true,
      hideInSearch: props.is_select,
      valueEnum: {
        "": {
          text: t('Account'),
        },
        "LDAP": {
          text: t('LDAP'),
        },
        "GOOGLE": {
          text: t('Google'),
        },
        "GITHUB": {
          text: t('Github'),
        },
        "OKTA": {
          text: t('Okta'),
        },
        "AZURE": {
          text: t('Microsoft'),
        },
        "FACEBOOK": {
          text: t('Facebook'),
        },
        "AUTH0": {
          text: t('Auth0'),
        },
        "GITLAB": {
          text: t('GitLab'),
        },
      },
    },
    {
      title: t('Note'),
      dataIndex: 'note',
      search: {
        transform: (value) => ({
          note: value,
        }),
      },
      valueType: 'textarea',
      key: 'a_note',
      sorter: true,
      sortOrder: props.sortState.columnKey === 'a_note' ? props.sortState.order : undefined,
      showSorterTooltip: false,
    },
    ...props.extraColumns || [],
  ];
  return columns;
}

const TableList: React.FC = () => {
  const [createModalVisible, handleCreateModalVisible] = useState<boolean>(false);
  const [inviteModalVisible, handleInviteModalVisible] = useState<boolean>(false);
  const [updateModalVisible, handleUpdateModalVisible] = useState<boolean>(false);
  const [delConfirmModalVisible, handleDelConfirmModalVisible] = useState<boolean>(false);
  const [assignModalVisible, handleAssignModalVisible] = useState<boolean>(false);
  const [disableEmailVerificationModalVisible, setDisableEmailVerificationModalVisible] = useState<boolean>(false);
  const [reset2FAModalVisible, setReset2FAModalVisible] = useState<boolean>(false);
  const [enable2FAEnforcementModalVisible, setEnable2FAEnforcementModalVisible] = useState<boolean>(false);
  const [disable2FAEnforcementModalVisible, setDisable2FAEnforcementModalVisible] = useState<boolean>(false);
  const [forceLogoutModalVisible, setForceLogoutModalVisible] = useState<boolean>(false);
  const [setNoteModalVisible, setSetNoteModalVisible] = useState<boolean>(false);
  const [sortState, setSortState] = useState<API.SortState>(() => getLocalSortState(USER_LIST_SORT_STATE_KEY));
  const { initialState } = useModel('@@initialState');

  const [showDetail, setShowDetail] = useState<boolean>(false);

  const actionRef = useRef<ActionType>();
  const [currentRow, setCurrentRow] = useState<API.UserListItem>();
  const [selectedRowsState, setSelectedRows] = useState<API.UserListItem[]>([]);
  const [moreMenuRowsState, setMoreMenuRows] = useState<API.UserListItem[]>([]);

  const [smtpValid, setSmtpValid] = useState<boolean>(false);
  const [usedCount, setUsedCount] = useState<API.UsedCount | undefined>();
  const isAdmin = initialState?.user?.is_admin;

  // Role selection state
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const showMultiSelect = shouldUserListShowMultiSelect(initialState?.user);

  useEffect(() => {
    if (smtpValid) return;
    (async () => {
      try {
        const res = await smtpConfigValidate();
        setSmtpValid(res);
      } catch (error) {
        setSmtpValid(false);
      }
    })();
  }, [smtpValid]);

  useEffect(() => {
    if (isAdmin) {
      refreshUsedCount();
    }
  }, []);

  // Load roles when modal opens
  useEffect(() => {
    if (isAdmin && (createModalVisible || updateModalVisible || inviteModalVisible)) {
      setLoadingRoles(true);
      fetchAllNames("%", "roles", "").then((roleNames) => {
        setAllRoles(roleNames);
        setLoadingRoles(false);
      }).catch(() => {
        setLoadingRoles(false);
      });
    }
  }, [createModalVisible, updateModalVisible, inviteModalVisible, isAdmin]);

  const refreshUsedCount = () => {
    getUsedCount().then(data => {
      setUsedCount(data);
    });
  }

  const newOrUpdateForm = (
    isNew: boolean,
    visible: boolean,
    handleModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
    pActionRef: React.MutableRefObject<ActionType | undefined>,
    initialValues: API.UserListItem | {},
  ) => {
    if (!visible) return null;
    const isMe = !isNew && currentRow?.name == initialState?.user?.name;

    // Check permissions for edit mode
    const canEditPassword = isNew || canEditUserPassword(initialState?.user, currentRow);
    const canEditEmail = isNew || canEditUserEmail(initialState?.user, currentRow);
    const canEditNote = isNew || canEditUserNote(initialState?.user, currentRow);
    const canEditDisplayName = isNew || (!isMe && canEditUserDisplayName(initialState?.user, currentRow));
    const canChangeUserGroup = isNew || canChangeGroupOfUser(initialState?.user, currentRow);
    const enableAssignControlRole = !isNew && canAssignControlRoleToUser(initialState?.user, currentRow);
    const enableAssignStrategy = !isNew && canAssignStrategyToUser(initialState?.user, currentRow);

    const showAdminRoleEditor = !isNew || isAdmin;
    const showControlRoleEditor = !isNew;
    const showStrategyEditor = !isNew;

    return (
      <ModalForm
        title={t(isNew ? 'Create' : 'Edit')}
        visible={visible}
        width="400px"
        initialValues={initialValues}
        onVisibleChange={handleModalVisible}
        onFinish={async (value) => {
          const success = await (isNew ? handleAdd : handleUpdate)(
            value as API.UserListItem,
            initialValues as API.UserListItem,
          );
          if (success) {
            handleModalVisible(false);
            if (pActionRef.current) {
              pActionRef.current.reload();
            }
            if (isNew) {
              refreshUsedCount();
            }
          }
        }}
      >
        <UserFields
          disabled={isMe}
          isNew={isNew}
          status={(initialValues as API.UserListItem).status}
          third_auth_type={(initialValues as API.UserListItem).third_auth_type}
          disablePassword={!canEditPassword}
          disableEmail={!canEditEmail}
          disableDisplayName={!canEditDisplayName}
          readonlyDisplayName={isMe}
        />
        <ProFormCheckbox disabled={isMe || !isAdmin} name={["is_admin"]}>{t("Administrator")}</ProFormCheckbox>
        <NameEditor table="grp" field="group_name" label="Group" required type={GroupTypeUser} disable={!isNew && !canChangeUserGroup} purpose={isNew ? NamesPurpose_CreateUser : NamesPurpose_ChangeUserGroup} />
        {showAdminRoleEditor && <ProFormSelect
          name="admin_roles"
          disabled={!isAdmin}
          label={t('Admin Roles')}
          width="md"
          mode="multiple"
          options={allRoles.map(name => ({ label: name, value: name }))}
          fieldProps={{
            loading: loadingRoles,
          }}
        />}
        {showControlRoleEditor && <NameEditor table="control_role" field="control_role_name" label="Control Role" disable={!enableAssignControlRole} purpose={isNew ? NamesPurpose_CreateUser : NamesPurpose_UpdateUser} />}
        {showStrategyEditor && <NameEditor table="strategy" field="strategy_name" label="Strategy" disable={!enableAssignStrategy} purpose={isNew ? NamesPurpose_CreateUser : NamesPurpose_UpdateUser} />}
        <ProFormTextArea
          fieldProps={{ autoComplete: 'off' }}
          width="md"
          name="note"
          disabled={!canEditNote}
          rules={[
            {
              max: 300,
            },
          ]}
          label={t('Note', true)}
        />
      </ModalForm>
    );
  };

  const inviteForm = (
    visible: boolean,
    handleModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
    pActionRef: React.MutableRefObject<ActionType | undefined>,
    initialValues: API.InviteUser | {},
  ) => {
    if (!visible) return null;
    return (
      <ModalForm
        title={t('Invite')}
        visible={visible}
        width="400px"
        initialValues={initialValues}
        onVisibleChange={handleModalVisible}
        onFinish={async (value) => {
          const hide = message.loading(t('Updating'));
          try {
            await inviteUser({ data: value });
            handleModalVisible(false);
            if (pActionRef.current) {
              pActionRef.current.reload();
            }
          } catch (error) {
            message.error(error as any);
          }
          hide();
        }}
      >
        <ProFormText
          fieldProps={{ autoComplete: 'off' }}
          width="md"
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
            },
            {
              min: 4,
              max: 30,
            },
          ]}
          label={t('Username', true)}
        />
        <ProFormText
          fieldProps={{ autoComplete: 'off' }}
          width="md"
          name="display_name"
          rules={[
            {
              max: 300,
            },
          ]}
          label={t('Display Name', true)}
        />
        <EmailEditor required />
        <ProFormSelect
          name="admin_roles"
          disabled={!isAdmin}
          label={t('Admin Roles')}
          width="md"
          mode="multiple"
          options={allRoles.map(name => ({ label: name, value: name }))}
          fieldProps={{
            loading: loadingRoles,
            placeholder: t('Select roles'),
          }}
        />
        <NameEditor table="grp" field="group_name" label="Group" required type={GroupTypeUser} purpose={NamesPurpose_InviteUser} />
        <ProFormTextArea
          fieldProps={{ autoComplete: 'off' }}
          width="md"
          name="note"
          rules={[
            {
              max: 300,
            },
          ]}
          label={t('Note', true)}
        />
      </ModalForm>
    );
  };

  const actionEnable = (record: API.UserListItem,) => {
    // Check permission
    if (!canEnableDisableUser(initialState?.user, record)) {
      return null;
    }

    return (
      initialState?.user?.name == record.name || record.status == UserStatus.Pending ? null :
        (<>
          <a
            style={record.status == UserStatus.Normal ? { color: "red" } : {}}
            onClick={async () => {
              setCurrentRow(record);
              await handleEnable([record], record.status == UserStatus.Normal);
              actionRef.current?.reloadAndRest?.();
              refreshUsedCount();
            }}
          >
            {t(record.status == UserStatus.Normal ? 'Disable' : 'Enable')}
          </a>
        </>)
    );
  };

  const actionDelete = (record: API.UserListItem,) => {
    // Check permission
    if (!canDeleteUser(initialState?.user, record)) {
      return null;
    }

    return (
      initialState?.user?.name == record.name || record.status == UserStatus.Normal ? null :
        (<>
          <a
            style={{ color: 'red' }}
            onClick={() => {
              setCurrentRow(record);
              handleDelConfirmModalVisible(true);
            }}
          >
            {t('Delete')}
          </a>
        </>)
    );
  };

  const createVerificationModal = (is2FA: boolean) => {
    const isVisible = is2FA ? reset2FAModalVisible : disableEmailVerificationModalVisible;
    const setVisible = is2FA ? setReset2FAModalVisible : setDisableEmailVerificationModalVisible;
    const title = is2FA ? t('reset_2fa_confirm') : t('disable_email_login_verification_confirm');
    const tip = is2FA ? t('reset_2fa_tip') : t('disable_email_login_verification_tip');
    const successMessage = is2FA ? '2FA configuration reset successfully!' : 'Email login verification disabled successfully!';
    const errorMessage = is2FA ? 'Failed to reset 2FA configuration!' : 'Failed to disable email login verification!';
    const type = is2FA ? '2fa' : 'email';

    const handleConfirm = async () => {
      try {
        await disableLoginVerification({ user_guids: moreMenuRowsState.map(r => r.guid), type });
        message.success(successMessage);
        setVisible(false);
        setSelectedRows([]);
        setMoreMenuRows([]);
        actionRef.current?.reloadAndRest?.();
      } catch (error) {
        message.error((typeof error == 'string' ? (error as string) : errorMessage));
      }
    };

    return (
      <Modal
        title={title}
        open={isVisible}
        onOk={handleConfirm}
        onCancel={() => setVisible(false)}
        okText={t('Confirm')}
        cancelText={t('Cancel')}
        okType="danger"
      >
        <div>
          {tip}
        </div>
      </Modal>
    );
  };

  const create2FAEnforcementModal = (enforce: boolean) => {
    const isVisible = enforce ? enable2FAEnforcementModalVisible : disable2FAEnforcementModalVisible;
    const setVisible = enforce ? setEnable2FAEnforcementModalVisible : setDisable2FAEnforcementModalVisible;
    const title = enforce ? t('enable_2fa_enforcement_confirm') : t('disable_2fa_enforcement_confirm');
    const tip = enforce ? t('2fa_enforcement_enable_tip') : t('2fa_enforcement_disable_tip');
    const successMessage = enforce ? '2FA enforcement has been enabled successfully!' : '2FA enforcement has been disabled successfully!';
    const errorMessage = enforce ? 'Failed to enable 2FA enforcement!' : 'Failed to disable 2FA enforcement!';
    const handleConfirm = async () => {
      try {
        await updateUserTFAEnforce({ user_guids: moreMenuRowsState.map(r => r.guid), enforce: enforce, url: location.origin });
        message.success(successMessage);
        setVisible(false);
        setSelectedRows([]);
        setMoreMenuRows([]);
        actionRef.current?.reloadAndRest?.();
      } catch (error) {
        message.error((typeof error == 'string' ? (error as string) : errorMessage));
      }
    };
    return (
      <Modal
        title={title}
        open={isVisible}
        onOk={handleConfirm}
        onCancel={() => setVisible(false)}
        okText={t('Confirm')}
        cancelText={t('Cancel')}
        okType="danger"
      >
        <div>
          {tip}
        </div>
      </Modal>
    );
  }

  const forceLogoutModal = () => {
    const handleConfirm = async () => {
      try {
        await forceLogout({ user_guids: moreMenuRowsState.map(r => r.guid) });
        message.success('Force logout successfully!');
        setForceLogoutModalVisible(false);
        setSelectedRows([]);
        setMoreMenuRows([]);
        actionRef.current?.reloadAndRest?.();
      } catch (error) {
        message.error((typeof error == 'string' ? (error as string) : 'Failed to logout!'));
      }
    }
    return (
      <Modal
        title={t('force_logout')}
        open={forceLogoutModalVisible}
        onOk={handleConfirm}
        onCancel={() => setForceLogoutModalVisible(false)}
        okText={t('Confirm')}
        cancelText={t('Cancel')}
        okType="danger"
      >
        <div>
          {t('force_logout_tip')}
          {moreMenuRowsState.some(r => r.name === initialState?.user?.name) && ' ' + t('force_logout_current_session_tip', true)}
        </div>
      </Modal>
    );
  }

  const setNoteModal = () => {
    return <SetNoteModal
      visible={setNoteModalVisible}
      setModalVisible={setSetNoteModalVisible}
      actionRef={actionRef}
      guids={moreMenuRowsState.map((x) => x.guid)}
      setFunction={async (guids, note) => {
        await setUsersNote({ data: { guids, note } });
        setSelectedRows([]);
        setMoreMenuRows([]);
      }}
    ></SetNoteModal >;
  }

  const actionMore = (records: API.UserListItem[], footer: boolean) => {
    if (records.length === 0) {
      return null;
    }
    const menuItems: ItemType[] = [];

    // Check if all records have the required permission
    const allHavePermission = (checkFunc: (currentUser: API.User | undefined,
      item: API.UserListItem | undefined) => boolean) => {
      return records.every(record => checkFunc(initialState?.user, record));
    };

    // Only admin can assign devices to user
    if (!footer && records.length === 1 && hasGlobalPermAssignDeviceToUser(initialState?.user)) {
      menuItems.push({
        key: 'assign-device',
        label: t('assign_devices'),
        onClick: () => {
          setCurrentRow(records[0]);
          handleAssignModalVisible(true);
        },
      });
    }

    const tfaItems: ItemType[] = [];
    // Check 2FA management permission
    if (allHavePermission(canManageUser2FA)) {
      if (records.some(r => !r.tfa_enforced)) {
        tfaItems.push({
          key: 'enable-2fa-enforcement',
          label: t('enable_2fa_enforcement'),
          onClick: () => {
            setMoreMenuRows(records);
            setEnable2FAEnforcementModalVisible(true);
          },
        });
      }

      if (records.some(r => r.tfa_enforced)) {
        tfaItems.push({
          key: 'disable-2fa-enforcement',
          label: t('disable_2fa_enforcement'),
          onClick: () => {
            setMoreMenuRows(records);
            setDisable2FAEnforcementModalVisible(true);
          },
        });
      }

      if (records.some(r => r.totp_verified && r.name !== initialState?.user?.name)) {
        tfaItems.push({
          key: 'reset-2fa',
          label: t('reset_2fa'),
          onClick: () => {
            if (records.some(r => r.name === initialState?.user?.name)) {
              message.error(t('disable_in_account_setting_tip'));
              return;
            }
            setMoreMenuRows(records);
            setReset2FAModalVisible(true);
          },
        });
      }
    }

    if (tfaItems.length > 0) {
      if (menuItems.length > 0) {
        menuItems.push({
          type: 'divider',
        });
      }
      menuItems.push(...tfaItems);
    }

    // Check 2FA/Email management permission
    if (allHavePermission(canManageUser2FA) &&
      records.some(r => r.email_verification && r.name !== initialState?.user?.name)) {
      if (menuItems.length > 0) {
        menuItems.push({
          type: 'divider',
        });
      }
      menuItems.push({
        key: 'disable-email',
        label: t('disable_email_login_verification'),
        onClick: () => {
          if (records.some(r => r.name === initialState?.user?.name)) {
            message.error(t('disable_in_account_setting_tip'));
            return;
          }
          setMoreMenuRows(records);
          setDisableEmailVerificationModalVisible(true);
        },
      });
    }

    // Check force logout permission
    if (allHavePermission(canForceLogoutUser)) {
      if (menuItems.length > 0) {
        menuItems.push({
          type: 'divider',
        });
      }
      menuItems.push({
        key: 'force-logout',
        label: t('force_logout'),
        onClick: () => {
          setMoreMenuRows(records);
          setForceLogoutModalVisible(true);
        },
      });
    }

    // Check edit note permission
    if (footer && allHavePermission(canEditUserNote)) {
      if (menuItems.length > 0) {
        menuItems.push({
          type: 'divider',
        });
      }
      menuItems.push({
        key: 'set-note',
        label: t('set_note'),
        onClick: () => {
          setMoreMenuRows(records);
          setSetNoteModalVisible(true);
        },
      });
    }

    const shouldShow = menuItems.length > 0;
    if (!shouldShow) {
      return null;
    }

    if (footer) {
      return (
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button>
            {t('More')}
          </Button>
        </Dropdown>
      );
    } else {
      return (
        <>
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <a onClick={(e) => e.preventDefault()}>
              {t('More')}
            </a>
          </Dropdown>
        </>
      );
    }
  };

  const columns = userColumns({
    actionRef,
    myname: initialState?.user?.name,
    is_select: false,
    onIdClick: (entity: API.UserListItem) => {
      setCurrentRow(entity);
      setShowDetail(true);
    },
    extraColumns: [
      {
        title: t('Action'),
        search: false,
        render: (text, record) => {
          // Check if user has any edit permission for this record
          const isMe = record.name == initialState?.user?.name;
          const hasAnyEditPerm =
            (!isMe && canEditUserEmail(initialState?.user, record)) ||
            (!isMe && canEditUserPassword(initialState?.user, record)) ||
            (!isMe && canEditUserDisplayName(initialState?.user, record)) ||
            canEditUserNote(initialState?.user, record) ||
            canChangeGroupOfUser(initialState?.user, record) ||
            canAssignControlRoleToUser(initialState?.user, record) ||
            canAssignStrategyToUser(initialState?.user, record);

          const editAction = hasAnyEditPerm ? (
            <a
              onClick={() => {
                setCurrentRow(record);
                handleUpdateModalVisible(true);
              }}
            >
              {t('Edit')}
            </a>
          ) : null;

          const enableAction = actionEnable(record);
          const deleteAction = actionDelete(record);
          const moreAction = actionMore([record], false);

          // Build actions array with dividers
          const actions: React.ReactNode[] = [];
          if (editAction) actions.push(editAction);
          if (enableAction) {
            if (actions.length > 0) actions.push(<Divider key="div1" type="vertical" />);
            actions.push(enableAction);
          }
          if (deleteAction) {
            if (actions.length > 0) actions.push(<Divider key="div2" type="vertical" />);
            actions.push(deleteAction);
          }
          if (moreAction) {
            if (actions.length > 0) actions.push(<Divider key="div3" type="vertical" />);
            actions.push(moreAction);
          }

          return <Fragment>{actions}</Fragment>;
        },
      }],
    sortState,
    setSortState,
  });

  const getHeaderTitle = () => {
    if (isAdmin) {
      return <Tooltip title={t('used_user')}>
        <span>
          {t('User List', true)} {(
            <span style={{ backgroundColor: getUsedUsersColor(usedCount, initialState?.license) }}>
              {getUsedUsers(usedCount, initialState?.license)}
            </span>
          )}
        </span>
      </Tooltip>;
    } else {
      return t('User List', true);
    }
  };

  return (
    <PageContainer>
      <ProTable<API.UserListItem, API.PageParams>
        headerTitle={getHeaderTitle()}
        columnsState={{ persistenceType: 'localStorage', persistenceKey: 'userlist_columns_state' }}
        actionRef={actionRef}
        rowKey="guid"
        search={{
          layout: 'vertical',
        }}
        toolBarRender={() => {
          const canCreate = hasPermUsersCreate(initialState?.user);
          const canInvite = hasPermUsersInvite(initialState?.user);
          const buttons = [];

          if (canCreate) {
            buttons.push(
              <Button
                type="primary"
                key="create"
                onClick={() => {
                  handleCreateModalVisible(true);
                }}
              >
                <PlusOutlined /> {t('Create')}
              </Button>
            );
          }

          if (canInvite) {
            buttons.push(
              <Tooltip
                key="invite"
                placement="bottom"
                title={smtpValid ? undefined : t('Please configure Email SMTP settings first')}
              >
                <Button
                  type="primary"
                  disabled={!smtpValid}
                  onClick={() => {
                    handleInviteModalVisible(true);
                  }}
                >
                  <PlusOutlined /> {t('Invite')}
                </Button>
              </Tooltip>
            );
          }

          return buttons;
        }}
        request={users}
        columns={columns}
        rowSelection={showMultiSelect ? {
          onChange: (_, selectedRows) => {
            setSelectedRows(selectedRows);
          },
        } : undefined}
        pagination={getTablePaginationConfig("user_list")}
        onChange={(_, __, sorter) => handleSortStateChange(sorter, actionRef, USER_LIST_SORT_STATE_KEY, setSortState)}
      />
      {selectedRowsState?.length > 0 && (() => {
        // Check if all selected users can be enabled/disabled
        const canEnableDisableAll = selectedRowsState.every(record =>
          canEnableDisableUser(initialState?.user, record)
        );
        const actionMoreButton = actionMore(selectedRowsState, true);
        if (!canEnableDisableAll && !actionMoreButton) {
          return null;
        }

        return (
          <FooterToolbar
            extra={
              <div>
                {t('Chosen')} <a style={{ fontWeight: 600 }}>{selectedRowsState.length}</a>{' '}
                {t('Items')}
              </div>
            }
          >
            {canEnableDisableAll && [true, false].map((v) => (
              <Button
                key={v ? 'enable' : 'disable'}
                onClick={async () => {
                  await handleEnable(selectedRowsState, !v);
                  setSelectedRows([]);
                  actionRef.current?.reloadAndRest?.();
                  refreshUsedCount();
                }}
              >
                {v ? t('Enable') : t('Disable')}
              </Button>
            ))}
            {actionMore(selectedRowsState, true)}
          </FooterToolbar>
        );
      })()}
      {newOrUpdateForm(true, createModalVisible, handleCreateModalVisible, actionRef, {})}
      {newOrUpdateForm(
        false,
        updateModalVisible,
        handleUpdateModalVisible,
        actionRef,
        currentRow || {},
      )}
      {inviteForm(inviteModalVisible, handleInviteModalVisible, actionRef, {})}
      {delConfirmModalVisible && <DelConfirmForm visible={delConfirmModalVisible} handleModalVisible={handleDelConfirmModalVisible} actionRef={actionRef} initialValues={currentRow || {}} del={delUser} />}
      {currentRow?.name !== undefined && <DeviceSelect
        visible={assignModalVisible}
        setVisible={handleAssignModalVisible}
        userGuid={currentRow.guid}
        userName={currentRow.name}
      ></DeviceSelect>}
      {renderDetails(
        showDetail,
        currentRow?.guid,
        currentRow,
        columns.slice(0, columns.length - 1),
        setCurrentRow,
        setShowDetail,
      )}
      {createVerificationModal(false)}
      {createVerificationModal(true)}
      {create2FAEnforcementModal(false)}
      {create2FAEnforcementModal(true)}
      {forceLogoutModal()}
      {setNoteModal()}
    </PageContainer>
  );
};

export default TableList;
